import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, Building2 } from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import {
  AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  AppButton, BackButton,
} from "../ui";
import { useBranch } from "../../contexts/BranchContext";
import "./branches.scss";

const initialState = {
  name: "",
  code: "",
  address: "",
  city: "",
  state: "",
  phone: "",
  email: "",
};

const AddOrEditBranchPage = () => {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const { branchId } = useParams();
  const navigate = useNavigate();
  const { refetchBranches } = useBranch();

  useEffect(() => {
    if (branchId) {
      setLoading(true);
      (async () => {
        try {
          const { data } = await apiClient.get(`/branches/${branchId}`);
          const b = data.branch || data;
          setForm({
            name: b.name || "",
            code: b.code || "",
            address: b.address || "",
            city: b.city || "",
            state: b.state || "",
            phone: b.phone || "",
            email: b.email || "",
          });
        } catch {
          setToast({ show: true, message: "Failed to load branch.", type: "error" });
        }
        setLoading(false);
      })();
    }
  }, [branchId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setToast({ show: true, message: "Branch name is required.", type: "error" });
      return;
    }
    setLoading(true);
    try {
      if (branchId) {
        await apiClient.put(`/branches/${branchId}`, form);
        setToast({ show: true, message: "Branch updated.", type: "success" });
      } else {
        await apiClient.post("/branches", form);
        setToast({ show: true, message: "Branch created.", type: "success" });
      }
      refetchBranches();
      setTimeout(() => navigate("/branches"), 1200);
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || "Failed to save branch.", type: "error" });
    }
    setLoading(false);
  };

  if (loading && branchId) {
    return <div className="branches-loading"><div className="spinner-border" /></div>;
  }

  return (
    <form className="branch-form-page" onSubmit={handleSubmit}>
      <CommonToaster toast={toast} onClose={() => setToast({ ...toast, show: false })} />

      {/* Header */}
      <div className="branch-form-header">
        <div className="branch-form-header__left">
          <BackButton onClick={() => navigate("/branches")} />
          <div>
            <h1 className="branch-form-header__title">
              {branchId ? "Edit Branch" : "Add New Branch"}
            </h1>
            <p className="branch-form-header__subtitle">
              {branchId ? `Editing ${form.name || "branch"}` : "Set up a new branch for your institute"}
            </p>
          </div>
        </div>
        <div className="branch-form-header__actions">
          <AppButton variant="outline" type="button" onClick={() => navigate("/branches")}>
            Cancel
          </AppButton>
          <AppButton variant="primary" type="submit" disabled={loading}>
            <Save size={16} />
            {loading ? "Saving..." : branchId ? "Update Branch" : "Save Branch"}
          </AppButton>
        </div>
      </div>

      {/* Form Body */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle className="bf-section-title">
            <Building2 size={16} /> Branch Details
          </AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          <div className="branch-form-grid">
            <div className="branch-form-field">
              <label className="branch-form-label">Branch Name *</label>
              <input
                className="branch-form-input"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g. Main Branch, Hyderabad Campus"
                required
              />
            </div>
            <div className="branch-form-field">
              <label className="branch-form-label">Branch Code</label>
              <input
                className="branch-form-input"
                name="code"
                value={form.code}
                onChange={handleChange}
                placeholder="e.g. HYD, MUM"
              />
            </div>
            <div className="branch-form-field branch-form-field--full">
              <label className="branch-form-label">Address</label>
              <input
                className="branch-form-input"
                name="address"
                value={form.address}
                onChange={handleChange}
                placeholder="Street address"
              />
            </div>
            <div className="branch-form-field">
              <label className="branch-form-label">City</label>
              <input
                className="branch-form-input"
                name="city"
                value={form.city}
                onChange={handleChange}
                placeholder="City"
              />
            </div>
            <div className="branch-form-field">
              <label className="branch-form-label">State</label>
              <input
                className="branch-form-input"
                name="state"
                value={form.state}
                onChange={handleChange}
                placeholder="State"
              />
            </div>
            <div className="branch-form-field">
              <label className="branch-form-label">Phone</label>
              <input
                className="branch-form-input"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="Contact number"
              />
            </div>
            <div className="branch-form-field">
              <label className="branch-form-label">Email</label>
              <input
                className="branch-form-input"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="branch@example.com"
              />
            </div>
          </div>
        </AppCardContent>
      </AppCard>
    </form>
  );
};

export default AddOrEditBranchPage;
